import { readJson, writeJson } from './storage'

export interface DemoLead {
  name: string
  email: string
  phone?: string
  score: number
  code: string
  lang: 'pl' | 'en'
}

/** Mirrors `ContactFormPayload` of onitsoft.com. */
interface ContactFormPayload {
  name: string
  email: string
  phone?: string
  subject: string
  message: string
}

const ENDPOINT = 'https://api.onitsoft.com/contact/onitsoft'
const QUEUE_KEY = 'leadQueue.v1'
const TIMEOUT_MS = 8000

let flushing = false

function toPayload(lead: DemoLead): ContactFormPayload {
  const when = new Date().toISOString()
  const message =
    lead.lang === 'en' ?
      [
        'Lead from the ONITSOFT trade-fair game (Trucking Game).',
        `Player: ${lead.name}`,
        `Score: ${lead.score}`,
        `Verification code: ${lead.code}`,
        `Submitted (UTC): ${when}`,
        'Requested: a Bobi / Sokar Logistics demo.',
      ].join('\n')
    : [
        'Zgłoszenie z targowej gry ONITSOFT (Trucking Game).',
        `Gracz: ${lead.name}`,
        `Wynik: ${lead.score}`,
        `Kod weryfikacyjny: ${lead.code}`,
        `Wysłano (UTC): ${when}`,
        'Prośba o demo Bobiego / Sokar Logistics.',
      ].join('\n')

  return {
    name: lead.name,
    email: lead.email,
    ...(lead.phone ? { phone: lead.phone } : {}),
    subject: lead.lang === 'en' ? 'Trade fair - game - demo (EN)' : 'Targi - gra - demo (PL)',
    message,
  }
}

/** One POST attempt. Resolves true only on a 2xx response. */
async function post(lead: DemoLead): Promise<boolean> {
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(toPayload(lead)),
      signal: AbortSignal.timeout(TIMEOUT_MS),
    })
    return res.ok
  } catch {
    return false
  }
}

function enqueue(lead: DemoLead): void {
  const queue = readJson<DemoLead[]>(QUEUE_KEY, [])
  queue.push(lead)
  writeJson(QUEUE_KEY, queue.slice(-50))
}

/** Posts the lead; on network failure it is queued in localStorage and 'queued' is returned. */
export async function submitLead(lead: DemoLead): Promise<'sent' | 'queued'> {
  if (await post(lead)) return 'sent'
  enqueue(lead)
  return 'queued'
}

/** Retries queued leads; safe to call on app start and on the window 'online' event. */
export async function flushLeadQueue(): Promise<void> {
  if (flushing) return
  flushing = true
  try {
    const queue = readJson<DemoLead[]>(QUEUE_KEY, [])
    if (!Array.isArray(queue) || queue.length === 0) return
    const kept: DemoLead[] = []
    for (const lead of queue) {
      if (!(await post(lead))) kept.push(lead)
    }
    // A lead submitted while we were flushing was appended after the batch we took.
    const current = readJson<DemoLead[]>(QUEUE_KEY, [])
    writeJson(QUEUE_KEY, [...kept, ...current.slice(queue.length)])
  } finally {
    flushing = false
  }
}
